// Helper functions for roadmap day navigation and mapping

export interface RoadmapDay {
  day: number;
  title: string;
  tasks: string[];
}

export interface RoadmapWeek {
  week: number;
  title: string;
  days: RoadmapDay[];
}

export interface DayData {
  week: number;
  day: number;
  title: string;
  tasks: string[];
  weekTitle: string;
  absoluteDayNumber: number;
}

/**
 * Convert week and day numbers to absolute day number (1-60)
 */
export function getAbsoluteDayNumber(week: number, day: number): number {
  return ((week - 1) * 7) + day;
}

/**
 * Convert absolute day number to week and day coordinates
 */
export function getDayCoordinates(dayNumber: number): { week: number; day: number } {
  const week = Math.ceil(dayNumber / 7);
  const day = dayNumber - ((week - 1) * 7);
  return { week, day };
}

/**
 * Get day data by absolute day number
 */
export function getDayByNumber(roadmap: RoadmapWeek[], dayNumber: number): DayData | null {
  const { week, day } = getDayCoordinates(dayNumber);
  
  const weekData = roadmap.find(w => w.week === week);
  if (!weekData) return null;
  
  const dayData = weekData.days.find(d => d.day === day);
  if (!dayData) return null;
  
  return {
    week,
    day,
    title: dayData.title,
    tasks: dayData.tasks,
    weekTitle: weekData.title,
    absoluteDayNumber: dayNumber,
  };
}

/**
 * Get total number of days in roadmap
 */
export function getTotalDays(roadmap: RoadmapWeek[]): number {
  return roadmap.reduce((total, week) => total + week.days.length, 0);
}

/**
 * Get adjacent day numbers (previous and next)
 */
export function getAdjacentDays(
  currentDayNumber: number,
  totalDays: number
): { previous: number | null; next: number | null } {
  return {
    previous: currentDayNumber > 1 ? currentDayNumber - 1 : null,
    next: currentDayNumber < totalDays ? currentDayNumber + 1 : null,
  };
}

/**
 * Get day completion key for database
 */
export function getDayKey(week: number, day: number): string {
  return `${week}-${day}`;
}

/**
 * Get the last uncompleted day number
 */
export function getLastUncompletedDay(
  roadmap: RoadmapWeek[],
  completedTasks: { [key: string]: boolean }
): number {
  const totalDays = getTotalDays(roadmap);
  
  for (let dayNum = 1; dayNum <= totalDays; dayNum++) {
    const { week, day } = getDayCoordinates(dayNum);
    const dayKey = getDayKey(week, day);
    
    if (!completedTasks[dayKey]) {
      return dayNum;
    }
  }
  
  // If all days completed, return last day
  return totalDays;
}

/**
 * Get all days as a flat array with metadata
 */
export function getAllDaysFlat(roadmap: RoadmapWeek[]): DayData[] {
  const days: DayData[] = [];
  
  roadmap.forEach(week => {
    week.days.forEach(day => {
      const absoluteDayNumber = getAbsoluteDayNumber(week.week, day.day);
      days.push({
        week: week.week,
        day: day.day,
        title: day.title,
        tasks: day.tasks,
        weekTitle: week.title,
        absoluteDayNumber,
      });
    });
  });
  
  return days;
}

/**
 * Normalize a roadmap to exactly 28 days (4 weeks) with sequential numbering
 */
export function normalizeRoadmapTo28Days(roadmap: RoadmapWeek[]): RoadmapWeek[] {
  // Flatten all days from all weeks
  const allDays: RoadmapDay[] = [];
  roadmap.forEach(week => {
    week.days.forEach(day => {
      allDays.push({
        day: day.day,
        title: day.title,
        tasks: day.tasks,
      });
    });
  });

  // Take only first 28 days
  const limitedDays = allDays.slice(0, 28);

  // Reorganize into 4 weeks with sequential numbering
  const normalizedWeeks: RoadmapWeek[] = [];
  for (let weekNum = 1; weekNum <= 4; weekNum++) {
    const weekDays: RoadmapDay[] = [];
    for (let dayNum = 1; dayNum <= 7; dayNum++) {
      const dayIndex = (weekNum - 1) * 7 + (dayNum - 1);
      if (dayIndex < limitedDays.length) {
        weekDays.push({
          day: dayNum,
          title: limitedDays[dayIndex].title,
          tasks: limitedDays[dayIndex].tasks,
        });
      }
    }
    
    if (weekDays.length > 0) {
      normalizedWeeks.push({
        week: weekNum,
        title: `Week ${weekNum}`,
        days: weekDays,
      });
    }
  }

  return normalizedWeeks;
}
